import { FeatureController, type BaseFeatureState, type PlayerAccessRights } from '@playcastdotio/FeatureController';
import { InputCaptureManager } from '../_base/InputCaptureManager';
import { BeaconTelemetryManager } from './managers/BeaconTelemetryManager';
import { connectBeaconFastStart, disconnect } from '../../api/modules/realtimeMessaging';
import { handleEvent } from '../../api/modules/hostMessaging/inputEvents';
import { beaconResolveState } from '../../api/stores/beaconStores';
import { playerConnectionState } from '../../api/stores/playerStores';
import { getPreFetchedIceServers } from '../../api/modules/iceServerPrefetch';
import {
  createPeer,
  closePeer,
  setPeerMilestoneCallbacks,
  isGuestReconnectInProgress,
  rejoinGuestSession,
  guestRecoveryBudgetRemainingMs,
  setPeerMediaPolicyManager,
} from '../../api/modules/peer';
import {
  GUEST_RECONNECT_UI_TIMEOUT_MS,
  GUEST_RECONNECT_MAX_ATTEMPTS,
  GUEST_RECOVERY_HARD_CEILING_MS,
  createSuspendableClock,
  createDocumentVisibilitySource,
  type SuspendableClock,
} from '@playcastdotio/PeerCore';
import { getPersistentPlayerId } from '../../api';
import { authStore } from '../../api/stores/globalStores';
import { GuestWebRtcPolicyManager } from '../webrtc-policy/GuestWebRtcPolicyManager';
import { whenHumanPageLoad } from '../../utils/prerenderGuard';

export type BeaconJoinPhase =
  | 'page_viewed'
  | 'fast_starting'
  | 'connecting_peer'
  /**
   * Recovering a session that was already playing, WITHOUT unmounting the player.
   * Deliberately not `connecting_peer`, which renders the pre-stream loading card: reusing
   * that for a retry tore down the video element, fullscreen and pointer lock and re-showed
   * "Click Here To Join" every time the transport hiccupped.
   */
  | 'reconnecting_in_place'
  | 'playing'
  | 'kicked'
  | 'disconnected'
  | 'not_found'
  | 'error';

export interface BeaconJoinState extends BaseFeatureState {
  phase: BeaconJoinPhase;
  shareCode: string;
  sessionId: string;
  hostPeerId?: string;
  peerId?: string;
  streamTitle?: string;
  isReconnecting: boolean;
  connectionRetries: number;
  kickReason?: string;
  access?: PlayerAccessRights;
}

/**
 * App-level rebuilds allowed per recovery episode. Taken from the shared PeerCore contract
 * rather than a local literal so "no more than two attempts" is one number honoured by every
 * layer — a second private copy is exactly how the layers drifted apart.
 */
const MAX_RECONNECT_ATTEMPTS = GUEST_RECONNECT_MAX_ATTEMPTS;

export class BeaconJoinCoordinator extends FeatureController<BeaconJoinState> {
  private analyticsManager: BeaconTelemetryManager;
  private inputCaptureManager: InputCaptureManager | null = null;
  private fastStartTimestamp = 0;
  private connectionStartTime = 0;
  private resolveListenerId?: string;
  private connectionListenerId?: string;
  private connectionTimeoutId?: NodeJS.Timeout;
  /**
   * True once this instance has actually initiated a fast start. Until then the
   * gate may still be pending (hidden / pre-render load) or React StrictMode may
   * have mounted→cleaned up→remounted, and there is no session to end.
   *
   * This gates transport teardown, NOT the "Session Ended" emit: a fast start that
   * reaches the server but never reaches a peer (fast-start `NOT_FOUND`, a generic
   * resolve error, or a terminal `debugPhase`) has still opened a socket, so
   * `disconnect()` is owed, but it never announced a session.
   */
  private hasStarted = false;

  /**
   * True only once a session has actually been announced to analytics — i.e. at the
   * single `sendSessionStarted()` call site in `onPeerConnected()` for a non-recovery
   * connection. A fast start that fails before a peer connects must not emit
   * "Session Ended" with no matching "Session Started".
   */
  private sessionAnnounced = false;

  /**
   * Suspendable episode clock spanning transport loss → live stream or terminal outcome.
   * Advances only while the tab is visible so a backgrounded tab does not burn the budget
   * while PeerCore still has budget left.
   */
  private readonly recoveryClock: SuspendableClock = createSuspendableClock();
  private recoveryEpisodeRunning = false;
  private unsubscribeVisibility: (() => void) | null = null;
  private documentHidden = false;
  private readonly mediaPolicy: GuestWebRtcPolicyManager;

  constructor(
    shareCode: string,
    debugPhase?: BeaconJoinPhase,
    mediaPolicy = new GuestWebRtcPolicyManager(),
  ) {
    const sessionId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
    const initialPhase = debugPhase ?? 'page_viewed';

    super({
      status: 'idle',
      phase: initialPhase,
      shareCode,
      sessionId,
      isReconnecting: false,
      connectionRetries: 0,
      access: { mouse: true, keyboard: true, touch: true, gamepad: true },
    });

    this.mediaPolicy = setPeerMediaPolicyManager(mediaPolicy);

    this.analyticsManager = new BeaconTelemetryManager({
      getState: () => this.state.currentState,
    });

    const visibility = createDocumentVisibilitySource();
    this.documentHidden = visibility.current() === 'hidden';
    this.unsubscribeVisibility = visibility.subscribe((state) => {
      this.documentHidden = state === 'hidden';
      if (state === 'hidden') this.recoveryClock.suspend();
      else this.recoveryClock.resume();
    });
  }

  protected async onInit(): Promise<void> {
    // Do not start a session or emit page-view analytics on a speculative load
    // (prefetch / pre-render) or while the page is hidden — wait for a real,
    // visible user first.
    await whenHumanPageLoad();
    if (this.isDestroyed) return;
    this.analyticsManager.sendPageViewed();
    try {
      await this.mediaPolicy.initialize();
    } catch (error) {
      // A terminal media-policy refusal (no hardware codec is possible) must surface as the
      // error card, not as `status: 'error'` with the phase still on "Connecting to host...".
      // Without this the guest waits forever with no timeout armed, because the connection
      // timeout only starts downstream of startFastStart().
      console.warn('[BeaconJoinCoordinator] Media policy unavailable:', error);
      this.transitionTo('error');
      return;
    }
    if (this.isDestroyed) return;
    this.startFastStart();
  }

  public transitionTo(phase: BeaconJoinPhase): void {
    if (this.isDestroyed) return;
    const previous = this.state.currentState.phase;
    if (this.isTerminalPhase(previous) && phase !== previous) {
      console.warn(`[BeaconJoinCoordinator] Blocked transition from terminal phase: ${previous} -> ${phase}`);
      return;
    }
    console.log(`[BeaconJoinCoordinator] ${previous} -> ${phase}`);
    this.state.updateState({ ...this.state.currentState, phase });
  }

  private startFastStart(): void {
    this.hasStarted = true;
    this.transitionTo('fast_starting');
    this.fastStartTimestamp = Date.now();
    this.analyticsManager.sendFastStartInitiated();

    this.resolveListenerId = beaconResolveState.addListener((resolveState) => {
      if (this.state.currentState.phase !== 'fast_starting') return;
      if (resolveState.status === 'resolved') {
        this.onFastStartSuccess(resolveState);
      } else if (resolveState.status === 'error') {
        this.onFastStartError(resolveState);
      }
    });
    this.addCleanup(() => {
      if (this.resolveListenerId) {
        beaconResolveState.removeListener(this.resolveListenerId);
      }
    });

    const currentResolve = beaconResolveState.currentState;
    if (currentResolve.status === 'resolved') {
      this.onFastStartSuccess(currentResolve);
      return;
    }
    if (currentResolve.status === 'error') {
      this.onFastStartError(currentResolve);
      return;
    }

    const persistentId = getPersistentPlayerId();
    const tokenJwt = authStore.currentState.accessToken || undefined;

    connectBeaconFastStart({
      shareCode: this.state.currentState.shareCode,
      persistentId,
      tokenJwt,
      requestIceServers: true,
    });
  }

  private async onFastStartSuccess(resolveState: typeof beaconResolveState.currentState): Promise<void> {
    const durationMs = Date.now() - this.fastStartTimestamp;
    this.analyticsManager.sendFastStartSucceeded(durationMs);

    const { hostPeerId, peerId, peerToken, iceServers, streamTitle } = resolveState;

    this.state.updateState({
      ...this.state.currentState,
      hostPeerId,
      peerId,
      streamTitle,
    });

    let finalIceServers = iceServers;
    if (!finalIceServers) {
      const prefetched = await getPreFetchedIceServers();
      if (prefetched) {
        finalIceServers = prefetched;
      }
    }

    playerConnectionState.updateState({
      ...playerConnectionState.currentState,
      playerPeerId: peerId!,
      peerToken,
      broadcastSessionId: hostPeerId!,
      iceServers: finalIceServers,
      status: 'idle',
      allowPeerConnections: true,
    });

    this.connectToPeer(hostPeerId!);
  }

  private onFastStartError(resolveState: typeof beaconResolveState.currentState): void {
    const durationMs = Date.now() - this.fastStartTimestamp;
    this.analyticsManager.sendFastStartFailed(
      resolveState.errorCode ?? 'UNKNOWN',
      resolveState.errorMessage ?? 'Unknown error',
      resolveState.errorStage ?? 'resolve',
    );

    if (resolveState.errorCode === 'NOT_FOUND') {
      this.analyticsManager.sendNotFound();
      this.transitionTo('not_found');
    } else {
      this.transitionTo('error');
    }
  }

  private connectToPeer(hostPeerId: string): void {
    this.transitionTo('connecting_peer');
    this.connectionStartTime = Date.now();
    this.analyticsManager.sendBroadcastJoinAttempted();

    setPeerMilestoneCallbacks({
      onPeerIdAcquired: (id) => this.analyticsManager.sendPeerIdAcquired(id),
      onSdpOfferSent: () => this.analyticsManager.sendSdpOfferSent(),
      onSdpAnswerReceived: () => this.analyticsManager.sendSdpAnswerReceived(),
      onIceCandidatesSent: (count) => this.analyticsManager.sendIceCandidatesSent(count),
      onIceChecking: () => this.analyticsManager.sendIceCheckingStarted(),
      onIceConnected: () => this.analyticsManager.sendIceConnected(),
      onRemoteStreamReceived: () => this.analyticsManager.sendRemoteStreamReceived(),
    });

    this.connectionListenerId = playerConnectionState.addListener((connState) => {
      const currentPhase = this.state.currentState.phase;
      if (this.isTerminalPhase(currentPhase)) return;

      const wasPlaying = currentPhase === 'playing' || currentPhase === 'reconnecting_in_place';

      if (connState.status === 'connected') {
        if (currentPhase === 'playing') {
          // ICE recovery completed while still `playing`: clear the episode so a future drop
          // gets a fresh budget. No phase transition needed — we never left `playing`.
          this.endRecoveryEpisode();
          this.state.updateState({ ...this.state.currentState, isReconnecting: false });
          return;
        }
        // For `reconnecting_in_place` and initial `connecting_peer`, go through the full
        // onPeerConnected path which clears timeouts, resets connectionRetries, and
        // transitions to `playing`.
        this.onPeerConnected();
      } else if (connState.status === 'reconnecting' && wasPlaying) {
        // The episode starts at transport loss, not at the first app-level rebuild — every
        // second PeerCore spends reclaiming is a second the player is already waiting.
        this.beginRecoveryEpisode();
        this.state.updateState({ ...this.state.currentState, isReconnecting: true });
      } else if (connState.status === 'refused') {
        this.onPlayerKicked(connState.kickReason || 'Refused by host');
      } else if (connState.status === 'failed') {
        this.onPeerConnectionFailed(connState.kickReason || 'Connection failed');
      } else if (connState.status === 'disconnected' && wasPlaying) {
        if (this.isKickReason(connState.kickReason)) {
          this.onPlayerKicked(connState.kickReason!);
        } else {
          this.onPeerDisconnectedDuringPlay(connState.kickReason);
        }
      } else if (connState.status === 'disconnected' && !wasPlaying) {
        this.onPeerConnectionFailed(connState.kickReason || 'Disconnected before playing');
      }
    });
    this.addCleanup(() => {
      if (this.connectionListenerId) {
        playerConnectionState.removeListener(this.connectionListenerId);
      }
    });

    this.armConnectionTimeout();

    createPeer(hostPeerId);
  }

  private isPeerCoreStillReconnecting(): boolean {
    return isGuestReconnectInProgress() || playerConnectionState.currentState.status === 'reconnecting';
  }

  /** Begin the episode if one is not already running; a retry inherits the first clock. */
  private beginRecoveryEpisode(): void {
    if (this.recoveryEpisodeRunning) return;
    this.recoveryEpisodeRunning = true;
    this.recoveryClock.start();
    // start() clears the suspended flag; re-suspend if the tab is already hidden.
    if (this.documentHidden) this.recoveryClock.suspend();
  }

  /** Forget the episode, so the next failure is timed from its own start. */
  private endRecoveryEpisode(): void {
    this.recoveryEpisodeRunning = false;
    this.recoveryClock.reset();
    if (this.documentHidden) this.recoveryClock.suspend();
  }

  /**
   * True while another in-place attempt still fits inside `GUEST_RECOVERY_HARD_CEILING_MS`.
   *
   * Two clocks, because neither alone is sufficient. PeerCore's episode is the authority
   * while a guest session exists, but a session the app has already closed reports a full
   * budget again, so the coordinator keeps its own clock across the close/rebuild boundary.
   * An `undefined` transport budget means "no session to ask", never "no budget" — reading it
   * as exhausted would refuse a rejoin at the moment it is most needed.
   *
   * The app clock is suspendable: hidden time does not burn budget.
   */
  private hasRecoveryBudgetLeft(): boolean {
    if (this.recoveryEpisodeRunning) {
      if (this.recoveryClock.elapsedMs() >= GUEST_RECOVERY_HARD_CEILING_MS) return false;
    }
    const transportRemaining = guestRecoveryBudgetRemainingMs();
    return transportRemaining === undefined || transportRemaining > 0;
  }

  private armConnectionTimeout(): void {
    this.clearConnectionTimeout();
    this.connectionTimeoutId = this.createTimeout(() => {
      if (this.isPeerCoreStillReconnecting()) {
        this.armConnectionTimeout();
        return;
      }
      this.onPeerConnectionFailed('Connection timeout');
    }, GUEST_RECONNECT_UI_TIMEOUT_MS);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutId) {
      clearTimeout(this.connectionTimeoutId);
      this.connectionTimeoutId = undefined;
    }
  }

  private onPeerConnected(): void {
    this.clearConnectionTimeout();
    // Capture whether this is a recovery or initial connection before transitioning.
    const isRecovery = this.state.currentState.phase === 'reconnecting_in_place';
    // A live stream ends the episode: a drop minutes from now is a new failure and deserves
    // a full budget, not the remainder of one that already succeeded.
    this.endRecoveryEpisode();
    // `connectionRetries` bounds CONSECUTIVE failed reconnects, so a session that
    // recovers must start its next failover from zero. Left un-reset it becomes a
    // session-lifetime counter and the third network drop is refused a reconnect
    // even though the first two recovered cleanly, latching the terminal
    // 'disconnected' phase. Mirrors GuestPeerSession's resetRetriesOnConnected.
    this.state.updateState({
      ...this.state.currentState,
      isReconnecting: false,
      connectionRetries: 0,
    });
    const duration = Date.now() - this.connectionStartTime;
    this.analyticsManager.sendBroadcastJoinSucceeded(duration);
    this.transitionTo('playing');

    this.startInputCapture();

    if (!isRecovery) {
      this.sessionAnnounced = true;
      this.analyticsManager.sendSessionStarted();
      this.addEventListenerSafe(document, 'visibilitychange', () => {
        this.analyticsManager.sendTabVisibilityChanged(!document.hidden);
      });
    }
  }

  private startInputCapture(): void {
    this.inputCaptureManager?.cleanup();

    this.inputCaptureManager = new InputCaptureManager({
      access: this.state.currentState.access ?? { mouse: true, keyboard: true, touch: true, gamepad: true },
      onKeyboard: (event) => handleEvent(event),
      onMouse: (event) => handleEvent(event),
      onTouch: (event) => handleEvent(event),
      onGamepad: (gamepads) => {
        const event = new CustomEvent('gamepad', { detail: gamepads });
        handleEvent(event);
      },
    });
    this.inputCaptureManager.start();
  }

  private isTerminalPhase(phase: BeaconJoinPhase): boolean {
    return phase === 'kicked' || phase === 'disconnected' || phase === 'error' || phase === 'not_found';
  }

  private isKickReason(reason?: string): boolean {
    if (!reason) return false;
    const lower = reason.toLowerCase();
    return lower.includes('kick') || lower.includes('refused') || lower.includes('host:close');
  }

  private isHostTerminalReason(reason?: string): boolean {
    if (!reason) return false;
    const lower = reason.toLowerCase();
    return (
      lower.includes('host:stream_stopped') ||
      lower.includes('host:shutdown') ||
      lower.includes('host:close') ||
      lower.includes('host:kick')
    );
  }

  private onPlayerKicked(reason: string): void {
    this.clearConnectionTimeout();
    this.analyticsManager.sendPlayerDisconnected(reason);
    this.state.updateState({
      ...this.state.currentState,
      kickReason: reason,
    });
    this.transitionTo('kicked');
  }

  private onPeerConnectionFailed(reason: string): void {
    if (this.isPeerCoreStillReconnecting()) {
      return;
    }
    this.clearConnectionTimeout();
    const duration = Date.now() - this.connectionStartTime;
    this.analyticsManager.sendBroadcastJoinFailed(reason, duration);
    this.transitionTo('error');
  }

  private onPeerDisconnectedDuringPlay(kickReason?: string): void {
    if (this.isPeerCoreStillReconnecting() && !this.isHostTerminalReason(kickReason)) {
      this.beginRecoveryEpisode();
      this.state.updateState({ ...this.state.currentState, isReconnecting: true });
      return;
    }
    this.clearConnectionTimeout();
    this.analyticsManager.sendPlayerDisconnected(kickReason);

    if (this.isHostTerminalReason(kickReason)) {
      this.state.updateState({ ...this.state.currentState, isReconnecting: false });
      this.transitionTo('disconnected');
      return;
    }

    this.beginRecoveryEpisode();

    const retries = this.state.currentState.connectionRetries;
    if (retries >= MAX_RECONNECT_ATTEMPTS || !this.hasRecoveryBudgetLeft()) {
      // Out of attempts or out of clock: the terminal error card is the correct outcome,
      // and reaching it promptly is the point. Another rebuild here is what stacked a third
      // and fourth ladder onto a budget that was already spent.
      this.giveUpOnRecovery();
      return;
    }

    void this.startAppLevelRejoin(retries + 1);
  }

  /**
   * Rebuild the session at the app level, keeping the player mounted.
   *
   * Routed through `rejoinGuestSession` rather than a bare `createPeer`: the facade resets
   * PeerCore's retry budget before rebuilding, so the new session gets its full reclaim
   * allowance instead of inheriting the exhausted counter that just gave up. Calling
   * `createPeer` directly handed the rebuilt session ZERO reclaims, which is why the second
   * drop of a bad episode went terminal instantly.
   */
  private async startAppLevelRejoin(attempt: number): Promise<void> {
    this.state.updateState({
      ...this.state.currentState,
      isReconnecting: true,
      connectionRetries: attempt,
    });
    this.analyticsManager.sendPlayerReconnecting(attempt);
    // Quickstart's reconnect re-enters connectToBroadcast and emits this same milestone, so
    // an equivalent recovery has the same funnel shape in both modes.
    this.analyticsManager.sendBroadcastJoinAttempted();
    this.transitionTo('reconnecting_in_place');
    this.connectionStartTime = Date.now();
    // Bound the attempt: without a timeout a rejoin that never lands leaves the player on a
    // frozen frame with no terminal outcome at all.
    this.armConnectionTimeout();

    // A reason the transport contract reads as retryable. The host-terminal and refused
    // cases have already returned above, so what is left is a transient loss by definition —
    // passing an unclassifiable raw reason through would have the facade refuse the rejoin
    // and dead-end a session that today reconnects.
    const started = await rejoinGuestSession('connection_lost:beacon_retry');
    if (started) return;
    // The rejoin could not start. Do not disturb a session that recovered on its own while
    // this was in flight.
    if (this.state.currentState.phase !== 'reconnecting_in_place') return;
    this.giveUpOnRecovery();
  }

  /** Terminal outcome for a session that cannot be recovered: the Reconnect error card. */
  private giveUpOnRecovery(): void {
    this.clearConnectionTimeout();
    this.endRecoveryEpisode();
    this.state.updateState({ ...this.state.currentState, isReconnecting: false });
    this.transitionTo('disconnected');
  }

  public disconnectPeer(reason: string): void {
    closePeer(reason);
  }

  public cleanup(): void {
    // Only account for a session this instance actually announced. A page destroyed
    // while the gate is still pending (StrictMode mount→cleanup→mount, or a load
    // that never became visible) has no session to end; nor does a fast start that
    // failed before any peer connected (NOT_FOUND / resolve error / terminal
    // debugPhase). Emitting "Session Ended" with no matching "Session Started"
    // skews the funnel. The disconnect is owed whenever we reached the transport
    // (`hasStarted`), even if no session was announced — `disconnect()` on a
    // never-opened socket is a no-op, so erring toward teardown is correct.
    if (this.hasStarted) {
      if (this.sessionAnnounced) {
        this.analyticsManager.sendSessionEnded('cleanup');
      }
      this.disconnectPeer('beacon:cleanup');
      disconnect();
    }
    this.inputCaptureManager?.cleanup();
    this.inputCaptureManager = null;
    setPeerMilestoneCallbacks({});
    this.analyticsManager.cleanup();
    this.unsubscribeVisibility?.();
    this.unsubscribeVisibility = null;
    this.endRecoveryEpisode();
    super.cleanup();
  }
}
